import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

const GOLD  = '#B8892A';
const GOLD_L = '#D4A843';
const BG    = '#FAFAF8';
const BG2   = '#F3EBD8';
const BD    = '#E8E0D0';
const INK   = '#110E0A';
const MUTED = '#6B5E4E';
const INK3  = '#8C7A68';
const INK4  = '#B0A090';

const HOUSES = [
  { name: 'おたからや', tags: ['腕表', '珠宝', '名包'] },
  { name: '大吉',      tags: ['腕表', '珠宝'] },
  { name: 'Eco Auc',  tags: ['腕表', '珠宝', '名包'] },
  { name: 'RS Auction',tags: ['腕表', '珠宝'] },
  { name: 'RK Auction',tags: ['腕表', '名包'] },
  { name: 'APRE',     tags: ['腕表', '珠宝'] },
  { name: 'JWA',      tags: ['腕表', '珠宝'] },
  { name: 'REVA',     tags: ['珠宝', '名包'] },
  { name: 'Timeless', tags: ['腕表'] },
  { name: 'NJ Auction',tags: ['名包', '珠宝'] },
  { name: '大黒屋',   tags: ['腕表', '名包', '珠宝'] },
];

const SERVICES = ['s1','s2','s3','s4','s5','s6'];
const STEP_ICONS = ['🔍','📝','🏮','✈️'];

// Lot carousel card
function LotCard({ lot, isMobile }) {
  const navigate = useNavigate();
  const img = lot?.image_url || lot?.images?.[0];
  const price = lot?.current_price_jpy ?? lot?.start_price_jpy;
  const priceStr = price ? `¥${Number(price).toLocaleString()}` : '—';

  return (
    <div
      onClick={() => navigate(`/lots/${lot.id}`)}
      style={{
        background: '#fff', border: `1px solid ${BD}`, borderRadius: 10,
        overflow: 'hidden', cursor: 'pointer',
        boxShadow: '0 4px 20px rgba(17,14,10,.07)',
        transition: 'transform .2s, box-shadow .2s',
        width: '100%',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(17,14,10,.12)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(17,14,10,.07)'; }}
    >
      <div style={{ position: 'relative', paddingTop: '75%', background: BG2, overflow: 'hidden' }}>
        {img
          ? <img src={img} alt={lot.title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          : <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36 }}>📦</div>
        }
        <div style={{ position: 'absolute', top: 8, left: 8, background: GOLD, color: '#fff', fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 3, letterSpacing: 1 }}>
          {'推荐'}
        </div>
      </div>
      <div style={{ padding: '12px 14px' }}>
        <div style={{ fontSize: 11, color: INK3, marginBottom: 4, fontFamily: 'var(--mono,monospace)', letterSpacing: 1 }}>
          {(lot.house_name || lot.house_id || '').toUpperCase()}
        </div>
        <div style={{ fontSize: 13, fontWeight: 500, color: INK, lineHeight: 1.45, marginBottom: 8, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {lot.title || lot.lot_number}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontFamily: 'var(--mono,monospace)', fontSize: 15, fontWeight: 700, color: GOLD }}>{priceStr}</div>
          {lot.overall_grade && (
            <div style={{ fontSize: 10, padding: '2px 7px', border: `1px solid ${BD}`, borderRadius: 3, color: INK3, fontFamily: 'var(--mono,monospace)' }}>
              {lot.overall_grade}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function LandingConsignment() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [lots, setLots] = useState([]);
  const [lotIdx, setLotIdx] = useState(0);
  const [liveCount, setLiveCount] = useState(null);
  const [reviewIdx, setReviewIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  // Fetch featured (推荐) lots for carousel
  useEffect(() => {
    fetch('/api/lots/featured?limit=8')
      .then(r => r.ok ? r.json() : null)
      .then(rows => {
        const arr = Array.isArray(rows) ? rows : (rows?.data || []);
        if (!arr.length) return;
        // featured 接口返回 title_zh/title_ja + image_urls(JSON)，归一化成 LotCard 期望的字段
        setLots(arr.map(l => {
          let img;
          try {
            const im = typeof l.image_urls === 'string' ? JSON.parse(l.image_urls) : (l.image_urls || []);
            img = im[0];
          } catch { img = undefined; }
          return { ...l, title: l.title_zh || l.title_ja || l.title, image_url: l.image_url || img };
        }));
      })
      .catch(() => {});
    // 在拍总数（统计栏用）
    fetch('/api/lots?status=published&limit=1')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.total != null) setLiveCount(data.total); })
      .catch(() => {});
  }, []);

  // Auto-rotate carousel
  useEffect(() => {
    if (lots.length < 2) return;
    timerRef.current = setInterval(() => {
      setLotIdx(i => (i + 1) % lots.length);
    }, 3500);
    return () => clearInterval(timerRef.current);
  }, [lots.length]);

  const px = isMobile ? '16px' : '32px';
  const maxW = 1200;

  const reviews = [
    { name: t('landingProxy.r1Name'), city: t('landingProxy.r1City'), item: t('landingProxy.r1Item'), text: t('landingProxy.r1Text'), stars: 5 },
    { name: t('landingProxy.r2Name'), city: t('landingProxy.r2City'), item: t('landingProxy.r2Item'), text: t('landingProxy.r2Text'), stars: 5 },
    { name: t('landingProxy.r3Name'), city: t('landingProxy.r3City'), item: t('landingProxy.r3Item'), text: t('landingProxy.r3Text'), stars: 5 },
  ];

  return (
    <div style={{ fontFamily: 'var(--sans)', background: BG, color: INK, overflowX: 'hidden' }}>


      {/* ── HERO ── */}
      <section style={{
        minHeight: isMobile ? 'auto' : '80vh',
        display: 'flex', alignItems: 'center',
        background: 'linear-gradient(160deg, #FBF7F0 0%, #F3EBD8 40%, #EDE2CC 70%, #F5EDD8 100%)',
        paddingTop: 0,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(184,137,42,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(184,137,42,.05) 1px,transparent 1px)', backgroundSize: '48px 48px', pointerEvents: 'none' }} />

        <div style={{ maxWidth: maxW, margin: '0 auto', padding: `${isMobile ? '36px' : '72px'} ${px}`, width: '100%', position: 'relative', zIndex: 2 }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 380px', gap: isMobile ? 32 : 56, alignItems: 'center' }}>

            {/* Left: text */}
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'var(--mono,monospace)', fontSize: 10, letterSpacing: 3, color: GOLD, marginBottom: 20 }}>
                <span style={{ width: 24, height: 1, background: GOLD }} />
                {t('landingProxy.badge')}
                <span style={{ width: 24, height: 1, background: GOLD }} />
              </div>

              <h1 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 30 : 50, fontWeight: 700, color: INK, lineHeight: 1.18, marginBottom: 20, letterSpacing: 1 }}>
                {t('landingProxy.heroTitle1')}<br/>
                <span style={{ color: GOLD }}>{t('landingProxy.heroTitle2')}</span>
              </h1>

              <p style={{ fontSize: isMobile ? 14 : 16, color: MUTED, lineHeight: 1.85, marginBottom: 32, maxWidth: 480 }}>
                {t('landingProxy.heroDesc')}
              </p>

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 40 }}>
                <button onClick={() => navigate('/auction-hub')} style={{
                  padding: isMobile ? '12px 28px' : '14px 36px',
                  background: GOLD, border: 'none', borderRadius: 4, cursor: 'pointer',
                  fontSize: isMobile ? 14 : 15, fontWeight: 600, color: '#fff',
                  boxShadow: '0 4px 16px rgba(184,137,42,.35)', transition: 'all .18s',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = '#A07A22'}
                  onMouseLeave={e => e.currentTarget.style.background = GOLD}
                >
                  {t('landingProxy.ctaBrowse')}
                </button>
                <button onClick={() => navigate('/help/bidding-guide')} style={{
                  padding: isMobile ? '12px 28px' : '14px 36px',
                  background: 'transparent', border: `1.5px solid ${GOLD}`, borderRadius: 4, cursor: 'pointer',
                  fontSize: isMobile ? 14 : 15, fontWeight: 500, color: GOLD, transition: 'all .18s',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(184,137,42,.08)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  {t('landingProxy.ctaGuide')}
                </button>
              </div>

              {/* Fee quick stats */}
              <div style={{ display: 'flex', gap: isMobile ? 20 : 36, flexWrap: 'wrap', paddingTop: 20, borderTop: `1px solid rgba(184,137,42,.2)` }}>
                {[
                  { label: t('landingProxy.feeWon'), value: '¥200' },
                  { label: t('landingProxy.feeLost'), value: '¥100' },
                  { label: t('landingProxy.feeShip'), value: '¥100' },
                  ...(liveCount != null ? [{ label: t('landingProxy.statsItems'), value: liveCount.toLocaleString() }] : []),
                ].map(item => (
                  <div key={item.label}>
                    <div style={{ fontFamily: 'var(--mono,monospace)', fontSize: isMobile ? 20 : 26, fontWeight: 700, color: GOLD, letterSpacing: 1 }}>{item.value}</div>
                    <div style={{ fontSize: 11, color: INK3, marginTop: 2, whiteSpace: 'nowrap' }}>{item.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: live lot carousel */}
            {!isMobile && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div style={{ fontFamily: 'var(--mono,monospace)', fontSize: 10, letterSpacing: 2, color: GOLD, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: GOLD, display: 'inline-block' }} />
                    {t('landingProxy.carouselTitle')}
                  </div>
                  {lots.length > 0 && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      {lots.slice(0, Math.min(lots.length, 6)).map((_, i) => (
                        <button key={i} onClick={() => { setLotIdx(i); clearInterval(timerRef.current); }}
                          style={{ width: i === lotIdx ? 16 : 6, height: 6, borderRadius: 3, border: 'none', cursor: 'pointer', padding: 0, transition: 'width .25s, background .25s', background: i === lotIdx ? GOLD : BD }} />
                      ))}
                    </div>
                  )}
                </div>

                {lots.length > 0 ? (
                  <div style={{ position: 'relative', minHeight: 320 }}>
                    {lots.slice(0, Math.min(lots.length, 6)).map((lot, i) => (
                      <div key={lot.id} style={{
                        position: i === 0 ? 'relative' : 'absolute', top: 0, left: 0, right: 0,
                        opacity: i === lotIdx ? 1 : 0,
                        transition: 'opacity .5s ease',
                        pointerEvents: i === lotIdx ? 'auto' : 'none',
                      }}>
                        <LotCard lot={lot} isMobile={false} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ height: 280, background: '#fff', border: `1px solid ${BD}`, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: INK4, fontSize: 13 }}>
                    {t('landingProxy.carouselTitle')}…
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <style>{`@keyframes lpulse { 0%,100%{opacity:1} 50%{opacity:.3} }`}</style>
      </section>

      {/* ── STEPS ── */}
      <section style={{ background: '#fff', borderTop: `1px solid ${BD}`, borderBottom: `1px solid ${BD}` }}>
        <div style={{ maxWidth: maxW, margin: '0 auto', padding: `${isMobile ? '40px' : '60px'} ${px}` }}>
          <div style={{ textAlign: 'center', marginBottom: 44 }}>
            <div style={{ fontFamily: 'var(--mono,monospace)', fontSize: 10, letterSpacing: 3, color: GOLD, marginBottom: 10 }}>PROCESS</div>
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 22 : 30, fontWeight: 700, color: INK }}>{t('landingProxy.stepsTitle')}</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4,1fr)', gap: isMobile ? 16 : 24 }}>
            {[1,2,3,4].map((n, i) => (
              <div key={n} style={{ padding: isMobile ? '20px 16px' : '28px 24px', border: `1px solid ${BD}`, borderRadius: 10, background: BG, position: 'relative' }}>
                <div style={{ fontFamily: 'var(--mono,monospace)', fontSize: 11, color: GOLD, letterSpacing: 2, marginBottom: 10 }}>
                  STEP {t(`landingProxy.step${n}Num`)}
                </div>
                <div style={{ fontSize: isMobile ? 24 : 30, marginBottom: 10 }}>{STEP_ICONS[i]}</div>
                <div style={{ fontSize: isMobile ? 14 : 15, fontWeight: 600, color: INK, marginBottom: 8 }}>{t(`landingProxy.step${n}Title`)}</div>
                <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.8 }}>{t(`landingProxy.step${n}Desc`)}</div>
                {/* Connector arrow (desktop only) */}
                {!isMobile && i < 3 && (
                  <div style={{ position: 'absolute', right: -13, top: '50%', transform: 'translateY(-50%)', color: INK4, fontSize: 18, zIndex: 1 }}>›</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEES ── */}
      <section style={{ background: BG }}>
        <div style={{ maxWidth: maxW, margin: '0 auto', padding: `${isMobile ? '40px' : '60px'} ${px}` }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div style={{ fontFamily: 'var(--mono,monospace)', fontSize: 10, letterSpacing: 3, color: GOLD, marginBottom: 10 }}>PRICING</div>
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 22 : 30, fontWeight: 700, color: INK, marginBottom: 10 }}>{t('landingProxy.feesTitle')}</h2>
            <p style={{ fontSize: 13, color: MUTED, maxWidth: 540, margin: '0 auto' }}>{t('landingProxy.feesSubtitle')}</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20, maxWidth: 800, margin: '0 auto' }}>
            {/* Included */}
            <div style={{ background: '#fff', border: `1px solid ${BD}`, borderRadius: 10, padding: '28px 28px' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: GOLD, letterSpacing: 2, fontFamily: 'var(--mono,monospace)', marginBottom: 16 }}>{t('landingProxy.feesIncluded').toUpperCase()}</div>
              {[t('landingProxy.feesTax'), t('landingProxy.feesCommission')].map(item => (
                <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <span style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(184,137,42,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: GOLD, flexShrink: 0 }}>✓</span>
                  <span style={{ fontSize: 14, color: INK }}>{item}</span>
                </div>
              ))}
              <div style={{ borderTop: `1px solid ${BD}`, paddingTop: 16, marginTop: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: GOLD, letterSpacing: 2, fontFamily: 'var(--mono,monospace)', marginBottom: 12 }}>{t('landingProxy.feesFixed').toUpperCase()}</div>
                {[
                  { label: t('landingProxy.feeWon'), value: '¥200' },
                  { label: t('landingProxy.feeLost'), value: '¥100' },
                  { label: t('landingProxy.feeShip'), value: '¥100' },
                ].map(item => (
                  <div key={item.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <span style={{ fontSize: 14, color: INK }}>{item.label}</span>
                    <span style={{ fontFamily: 'var(--mono,monospace)', fontSize: 15, fontWeight: 700, color: GOLD }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Not charged */}
            <div style={{ background: '#fff', border: `1px solid ${BD}`, borderRadius: 10, padding: '28px 28px' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#888', letterSpacing: 2, fontFamily: 'var(--mono,monospace)', marginBottom: 16 }}>{t('landingProxy.feesNot').toUpperCase()}</div>
              {[t('landingProxy.feesNoHidden'), t('landingProxy.feesNoExchange'), t('landingProxy.feesNoExtra')].map(item => (
                <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <span style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(200,50,50,.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#C82032', flexShrink: 0 }}>✕</span>
                  <span style={{ fontSize: 14, color: INK }}>{item}</span>
                </div>
              ))}
              <div style={{ marginTop: 24, padding: '16px 18px', background: 'rgba(184,137,42,.05)', borderRadius: 6, borderLeft: `3px solid ${GOLD}` }}>
                <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.75 }}>
                  {t('landingProxy.feesSubtitle')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── AUCTION HOUSES ── */}
      <section style={{ background: '#fff', borderTop: `1px solid ${BD}`, borderBottom: `1px solid ${BD}` }}>
        <div style={{ maxWidth: maxW, margin: '0 auto', padding: `${isMobile ? '36px' : '52px'} ${px}` }}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ fontFamily: 'var(--mono,monospace)', fontSize: 10, letterSpacing: 3, color: GOLD, marginBottom: 8 }}>PARTNERS</div>
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 20 : 26, fontWeight: 700, color: INK }}>{t('landingProxy.housesTitle')}</h2>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
            {HOUSES.map(h => (
              <div key={h.name} style={{
                padding: '8px 18px', border: `1px solid ${BD}`, borderRadius: 20,
                fontSize: 13, color: MUTED, background: BG,
                fontFamily: 'var(--mono,monospace)', letterSpacing: 1,
                transition: 'border-color .15s, color .15s',
                cursor: 'default',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = BD; e.currentTarget.style.color = MUTED; }}
              >{h.name}</div>
            ))}
          </div>
        </div>
      </section>

      {/* ── POST-AUCTION SERVICES ── */}
      <section style={{ background: BG }}>
        <div style={{ maxWidth: maxW, margin: '0 auto', padding: `${isMobile ? '40px' : '56px'} ${px}` }}>
          <div style={{ textAlign: 'center', marginBottom: 36 }}>
            <div style={{ fontFamily: 'var(--mono,monospace)', fontSize: 10, letterSpacing: 3, color: GOLD, marginBottom: 8 }}>SERVICES</div>
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 20 : 26, fontWeight: 700, color: INK, marginBottom: 6 }}>{t('landingProxy.servicesTitle')}</h2>
            <p style={{ fontSize: 13, color: MUTED }}>{t('landingProxy.servicesSubtitle')}</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(6,1fr)', gap: 12, maxWidth: 900, margin: '0 auto' }}>
            {['🔬','💍','🧼','🔧','✨','📦'].map((icon, i) => (
              <div key={i} style={{ padding: '20px 12px', background: '#fff', border: `1px solid ${BD}`, borderRadius: 8, textAlign: 'center' }}>
                <div style={{ fontSize: 26, marginBottom: 8 }}>{icon}</div>
                <div style={{ fontSize: 11, color: INK, fontWeight: 500, lineHeight: 1.4 }}>{t(`landingProxy.s${i+1}`)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── REVIEWS ── */}
      <section style={{ background: '#fff', borderTop: `1px solid ${BD}`, borderBottom: `1px solid ${BD}` }}>
        <div style={{ maxWidth: maxW, margin: '0 auto', padding: `${isMobile ? '40px' : '60px'} ${px}` }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div style={{ fontFamily: 'var(--mono,monospace)', fontSize: 10, letterSpacing: 3, color: GOLD, marginBottom: 10 }}>TESTIMONIALS</div>
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 22 : 28, fontWeight: 700, color: INK }}>{t('landingProxy.reviewsTitle')}</h2>
          </div>

          {isMobile ? (
            /* Mobile: single card + dots */
            <div>
              <div style={{ background: BG, border: `1px solid ${BD}`, borderRadius: 10, padding: '24px 20px' }}>
                <div style={{ fontSize: 13, color: GOLD, marginBottom: 10, letterSpacing: 1 }}>{'★'.repeat(reviews[reviewIdx].stars)}</div>
                <p style={{ fontSize: 14, color: INK, lineHeight: 1.85, marginBottom: 16 }}>「{reviews[reviewIdx].text}」</p>
                <div style={{ fontSize: 12, color: INK3 }}>{reviews[reviewIdx].name} · {reviews[reviewIdx].city}</div>
                <div style={{ fontSize: 11, color: INK4, marginTop: 4 }}>{reviews[reviewIdx].item}</div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                {reviews.map((_, i) => (
                  <button key={i} onClick={() => setReviewIdx(i)} style={{ width: i === reviewIdx ? 16 : 6, height: 6, borderRadius: 3, border: 'none', cursor: 'pointer', padding: 0, background: i === reviewIdx ? GOLD : BD, transition: 'all .25s' }} />
                ))}
              </div>
            </div>
          ) : (
            /* Desktop: 3 cards side by side */
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20 }}>
              {reviews.map((r, i) => (
                <div key={i} style={{ background: BG, border: `1px solid ${BD}`, borderRadius: 10, padding: '28px 24px', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: 13, color: GOLD, marginBottom: 12, letterSpacing: 1 }}>{'★'.repeat(r.stars)}</div>
                  <p style={{ fontSize: 13, color: INK, lineHeight: 1.85, flex: 1, marginBottom: 20 }}>「{r.text}」</p>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: INK }}>{r.name} <span style={{ color: INK4 }}>· {r.city}</span></div>
                    <div style={{ fontSize: 11, color: INK4, marginTop: 4 }}>{r.item}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{
        background: 'linear-gradient(135deg, #1A1410 0%, #2C2016 100%)',
        padding: `${isMobile ? '52px' : '80px'} ${px}`,
      }}>
        <div style={{ maxWidth: maxW, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ fontFamily: 'var(--mono,monospace)', fontSize: 10, letterSpacing: 4, color: GOLD_L, marginBottom: 16 }}>START NOW</div>
          <h2 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 24 : 36, fontWeight: 700, color: '#fff', marginBottom: 14 }}>
            {t('landingProxy.ctaTitle')}
          </h2>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,.55)', marginBottom: 36, maxWidth: 460, margin: '0 auto 36px' }}>
            {t('landingProxy.ctaDesc')}
          </p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate(isLoggedIn ? '/browse' : '/register')} style={{
              padding: isMobile ? '12px 32px' : '14px 44px',
              background: GOLD, border: 'none', borderRadius: 4, cursor: 'pointer',
              fontSize: 15, fontWeight: 600, color: '#fff', boxShadow: '0 4px 20px rgba(184,137,42,.4)',
              transition: 'background .15s',
            }}
              onMouseEnter={e => e.currentTarget.style.background = '#A07A22'}
              onMouseLeave={e => e.currentTarget.style.background = GOLD}
            >
              {isLoggedIn ? t('landingProxy.ctaBrowseBtn') : t('landingProxy.ctaRegister')}
            </button>
            <button onClick={() => navigate('/auction-hub')} style={{
              padding: isMobile ? '12px 32px' : '14px 44px',
              background: 'transparent', border: '1.5px solid rgba(255,255,255,.3)', borderRadius: 4, cursor: 'pointer',
              fontSize: 15, color: 'rgba(255,255,255,.8)', transition: 'border-color .15s',
            }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,.7)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,.3)'}
            >
              {t('landingProxy.ctaAuctionBtn', '进入拍场')}
            </button>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ background: '#F2EBD9', borderTop: `1px solid ${BD}`, padding: `20px ${px}`, textAlign: 'center', fontSize: 12, color: MUTED, fontFamily: 'var(--mono,monospace)', letterSpacing: 1 }}>
        © {new Date().getFullYear()} 云川中古 · Japan Auction Proxy
      </footer>
    </div>
  );
}
